import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Typography,
  Box,
  Skeleton,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ImageOutlined as ImageIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import { Action } from '../types/action';
import { useSignedUrls } from './ActionsList';
import { SendActionDialog, SendActionDialogProps } from './SendActionDialog';
import { useTranslation } from 'react-i18next';

interface ActionCardProps {
  action: Action;
  onEdit?: () => void;
  deviceId: string;
  /** What this send would replace on the tablet (BA-85 D2, P2), passed on to SendActionDialog */
  replaces?: SendActionDialogProps['replaces'];
  /** What is on the tablet is not known yet (n2) */
  sendDisabled?: boolean;
}

export const ActionCard: React.FC<ActionCardProps> = ({ action, onEdit, deviceId, replaces = null, sendDisabled = false }) => {
  const { t } = useTranslation();
  const { signedUrls, loading: batchLoading } = useSignedUrls();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  // Use batch-fetched URL from context
  const batchUrl = signedUrls[action.id];
  const hasPicture = !!(action.thumbnailUrl || action.pictureUrl);

  useEffect(() => {
    // No picture to load
    if (!hasPicture) {
      setImageLoading(false);
      setImageUrl(null);
      return;
    }

    // Batch URL is available - use it
    if (batchUrl) {
      setImageUrl(batchUrl.url);
      setImageLoading(false);
      return;
    }

    // Batch is still loading - show loading state and wait
    if (batchLoading) {
      setImageLoading(true);
      return;
    }

    // Batch finished but no URL for this action - this shouldn't happen normally
    // but could occur if a new action was added after batch fetch completed
    // In this case, keep loading state (the batch will re-fetch when actions change)
    setImageLoading(false);
    setImageUrl(null);
  }, [batchUrl, batchLoading, hasPicture]);

  const handlePlayClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click event
    setSendDialogOpen(true);
  };

  const handleDialogClose = () => {
    setSendDialogOpen(false);
  };

  return (
    <>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: onEdit ? 'pointer' : 'default',
          '&:hover': onEdit ? {
            boxShadow: 6,
            transform: 'translateY(-4px)',
            transition: 'all 0.3s ease-in-out',
          } : {},
        }}
        onClick={onEdit}
      >
      {/* Picture/Thumbnail */}
      {hasPicture ? (
        imageLoading ? (
          <Skeleton variant="rectangular" height={200} />
        ) : imageUrl ? (
          <CardMedia
            component="img"
            height="200"
            image={imageUrl}
            alt={action.name}
            sx={{
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            sx={{
              height: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'grey.200',
            }}
          >
            <ImageIcon sx={{ fontSize: 64, color: 'grey.400' }} />
          </Box>
        )
      ) : (
        <Box
          sx={{
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'grey.200',
          }}
        >
          <ImageIcon sx={{ fontSize: 64, color: 'grey.400' }} />
        </Box>
      )}

      {/* Content */}
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h3" gutterBottom noWrap>
          {action.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {action.description}
        </Typography>
      </CardContent>

      {/* Play button */}
      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
        <Tooltip title={t('actions.sendActionToDevice')}>
          <IconButton
            color="primary"
            onClick={handlePlayClick}
            disabled={sendDisabled}
            aria-label="send action"
          >
            <PlayArrowIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>

    {/* Send Action Dialog */}
    <SendActionDialog
      open={sendDialogOpen}
      onClose={handleDialogClose}
      deviceId={deviceId}
      action={action}
      replaces={replaces}
    />
    </>
  );
};
